// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyCustomer, TyLangValue, TyLead } from "@/types";
import { leadTitle, pressToOpen, readLangValue, rightClickToOpen, stageTone } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "../selection.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    customer: TyCustomer;
    onOpen: () => void;
    onContact: (lead: TyLead) => void;
    /** What to do with somebody who has no live enquiry — which is most of a mature list. */
    onNewInterest: () => void;
    onActions: (anchor: DOMRect) => void;
    /** Picked by the bulk bar. Draws the ring; the screen decides what pressing the card then means. */
    isSelected?: boolean;
    /**
     * The bar is up. Every other control on the card is **gone, not disabled** — a half-live card
     * looks pressable and does something nobody asked for — and the menu button's place is taken by
     * the tick, so nothing moves and nothing overlaps.
     */
    isPicking?: boolean;
};

/**
 * One person, as a row of work rather than a row of tags.
 *
 * It used to print "lead · 1 open · 1 leads", which is three ways of saying a number and no way of
 * saying anything. Nobody opens this list to count; they open it because somebody rang, or because
 * a deal needs chasing. So the card answers **who**, **where are they up to**, and **what can I do
 * about it now** — in that order, one per row.
 *
 * The middle row is the whole point. A person with a live enquiry shows that enquiry and its stage;
 * a buyer with none shows what they bought instead, because "client" on its own tells you nothing
 * you would not guess from the badge beside their name.
 *
 * The picture is optional and usually absent — `Avatar` falls back to the initial — but a sales
 * team that meets people remembers faces, and four photographs in a list of twenty make it
 * scannable in a way twenty identical rows never are.
 *
 * **The whole card opens the person**, like every other list here. Hunting for the one word that
 * was a link is a tax on every row; the two buttons that mean something else stop the click.
 *
 * **Row three always has a primary action.** A person with a live enquiry gets *contact*; one
 * without gets *new interest*, because that is genuinely the next thing to do with a past buyer or
 * a cold name — and because the row previously collapsed to a lone menu button floating at the end
 * of a wide blank. Different answer, same shape.
 */
export default function CustomerCard({ customer, onOpen, onContact, onNewInterest, onActions, isSelected, isPicking }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    /** The live enquiry, if there is one. The list endpoint sends at most one — the newest open. */
    const open = customer.openLead ?? null;
    const owned = customer.ownedUnits ?? [];

    return (
        <article
            className={`customer-card pick${isSelected ? " pick--on" : ""}${isPicking ? " pick--picking" : ""}`}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={isPicking ? undefined : rightClickToOpen(onActions)}
        >
            {/* Row 1 — who. */}
            <div className="customer-card__who">
                <Avatar name={customer.name} picture={customer.picture} size={44} bordered />

                <span className="customer-card__identity">
                    <span className="customer-card__name">{customer.name}</span>
                    <bdi className="customer-card__phone" dir="ltr">
                        {customer.phone}
                    </bdi>
                </span>

                {customer.status === "client" && <StatusChip size="sm" tone="available" label={t.crm.statuses.client} />}
                {customer.isBlocked && <StatusChip size="sm" tone="danger" label={t.crm.blocked} />}
            </div>

            {/* Row 2 — where they are up to. */}
            <div className="customer-card__state">
                {open ? (
                    <>
                        <StatusChip size="sm" tone={stageTone(open.stage)} label={t.crm.stages[open.stage]} />
                        <span className="customer-card__want">{leadTitle(open, t, (value) => readLangValue(value as TyLangValue[], lang))}</span>
                    </>
                ) : owned.length ? (
                    <>
                        <span className="customer-card__owns">{t.crm.ownsUnit}</span>
                        <span className="customer-card__want">{owned.join("، ")}</span>
                    </>
                ) : (
                    <span className="customer-card__want customer-card__want--quiet">{t.crm.noOpenLead}</span>
                )}
            </div>

            {/* Row 3 — what can be done now. The card itself is the way to the profile, like every
                other list in the app, so only the two things that are *not* "open this" are here —
                and both stop the click reaching the card. */}
            <div className="customer-card__actions">
                {isPicking ? (
                    <span className={`pick__box${isSelected ? " pick__box--on" : ""}`} aria-hidden="true">
                        <Icon name="check" size={18} />
                    </span>
                ) : (
                    <>
                        <button
                            type="button"
                            className="customer-card__action customer-card__action--primary"
                            onClick={(event) => {
                                event.stopPropagation();
                                if (open) onContact(open);
                                else onNewInterest();
                            }}
                        >
                            {open ? t.crm.actions.contact : t.crm.addLead}
                        </button>

                        <button
                            type="button"
                            className="customer-card__more"
                            onClick={(event) => {
                                event.stopPropagation();
                                onActions(event.currentTarget.getBoundingClientRect());
                            }}
                            aria-label={t.common.more}
                        >
                            <Icon name="more" size={18} />
                        </button>
                    </>
                )}
            </div>
        </article>
    );
}
